import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Chip, CircularProgress, Divider, Drawer, IconButton, Link, Stack, Tooltip, Typography } from '@mui/material';
import { CloseCircle, DocumentText } from 'iconsax-react';

import useApi from 'hooks/useApi';
import { diApi } from '../api';
import type { DocumentCategory, InboundDocumentDetail, InboundDocumentPart } from '../types';
import { CATEGORY_LABELS, CATEGORY_ORDER, DocumentStatusChip, FILE_STATE_LABELS, KEY_FIELD_LABELS } from './documentLabels';
import { errorText, formatDateTime, relativeTime } from './format';

interface Props {
  clientUid: string;
  pro: string | null;
  /** The backend can ask the Lambda to re-read (prod only). */
  canRequest: boolean;
  onClose: () => void;
}

const mono = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: 12 };

/** Field rows of the summary, in reading order. `path` matches fieldSources / missingFields. */
const FIELD_GROUPS: Array<{ title: string; rows: Array<{ label: string; path: string }> }> = [
  {
    title: 'Carrier',
    rows: [
      { label: 'Vendor', path: 'vendorName' },
      { label: 'MC #', path: 'mcNumber' },
      { label: 'DOT #', path: 'dotNumber' }
    ]
  },
  {
    title: 'Truck',
    rows: [
      { label: 'Unit #', path: 'truck.number' },
      { label: 'Licence plate', path: 'truck.licensePlate' },
      { label: 'VIN', path: 'truck.vin' }
    ]
  },
  {
    title: 'Trailer',
    rows: [
      { label: 'Trailer #', path: 'trailer.number' },
      { label: 'Licence plate', path: 'trailer.licensePlate' },
      { label: 'VIN', path: 'trailer.vin' }
    ]
  },
  {
    title: 'Driver 1',
    rows: [
      { label: 'Name', path: 'driver1.name' },
      { label: 'Phone', path: 'driver1.phone' },
      { label: 'CDL #', path: 'driver1.licenseNumber' }
    ]
  },
  {
    title: 'Driver 2',
    rows: [
      { label: 'Name', path: 'driver2.name' },
      { label: 'Phone', path: 'driver2.phone' },
      { label: 'CDL #', path: 'driver2.licenseNumber' }
    ]
  },
  { title: 'Seal', rows: [{ label: 'Seal #', path: 'sealNumber' }] }
];

const BOL_ROWS: Array<{ label: string; key: string }> = [
  { label: 'BOL #', key: 'bolNumber' },
  { label: 'PRO #', key: 'proNumber' },
  { label: 'Customer ref', key: 'customerReference' },
  { label: 'Pickup ref', key: 'pickupReference' },
  { label: 'Delivery ref', key: 'deliveryReference' },
  { label: 'Container', key: 'containerNumber' },
  { label: 'Ship date', key: 'shipDate' },
  { label: 'Ship from', key: 'shipFrom' },
  { label: 'Ship to', key: 'shipTo' },
  { label: 'Pieces', key: 'pieces' },
  { label: 'Pallets', key: 'palletCount' },
  { label: 'Weight', key: 'weight' },
  { label: 'Tracker #', key: 'trackerNumber' }
];

const get = (o: any, path: string): any => path.split('.').reduce((a, k) => (a == null ? a : a[k]), o);
const isImage = (p: InboundDocumentPart) => p.mimeType === 'image/jpeg' || p.mimeType === 'image/png';

function Section({ title, children, action }: { title: string; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <Box component="section">
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle1">{title}</Typography>
        {action}
      </Stack>
      {children}
    </Box>
  );
}

export default function DocumentDetailDrawer({ clientUid, pro, canRequest, onClose }: Props) {
  const navigate = useNavigate();
  const { showToast } = useApi();
  const toastRef = useRef(showToast);
  toastRef.current = showToast;
  const [doc, setDoc] = useState<InboundDocumentDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rereading, setRereading] = useState(false);
  /** Load number a re-read was just requested for; the button stays off until its refresh. */
  const [rereadPending, setRereadPending] = useState<string | null>(null);
  const rereadTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (rereadTimer.current) clearTimeout(rereadTimer.current);
    },
    []
  );
  useEffect(() => {
    if (!pro && rereadTimer.current) {
      clearTimeout(rereadTimer.current);
      rereadTimer.current = null;
      setRereadPending(null);
    }
  }, [pro]);
  const [tick, setTick] = useState(0);
  const [showInfo, setShowInfo] = useState(false);
  const [showOther, setShowOther] = useState(false);

  useEffect(() => {
    // On close, keep the last load on screen while the drawer slides out.
    if (!pro) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setDoc((d) => (d && d.pro === pro ? d : null));
    diApi
      .getDocument(clientUid, pro)
      .then((res) => {
        if (cancelled) return;
        if (res.success === false) setError(res.message || 'Could not load this load.');
        else setDoc(res.data);
      })
      .catch((err) => !cancelled && setError(errorText(err, 'Could not load this load.')))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [clientUid, pro, tick]);

  // "fileId" or "fileId#partIndex" → what that source is, for "from …" labels and flags.
  const sources = useMemo(() => {
    const m = new Map<string, { category: DocumentCategory | null; name: string }>();
    for (const f of doc?.files ?? []) {
      // Flags about a whole file (failed, rejected) point at the file id itself.
      m.set(f.id, { category: f.parts?.length === 1 ? f.parts[0].category : null, name: f.name });
      for (const p of f.parts ?? []) {
        const entry = { category: p.category, name: f.parts.length > 1 ? `${f.name} › ${p.name}` : f.name };
        m.set(f.parts.length > 1 ? `${f.id}#${p.index}` : f.id, entry);
      }
    }
    return m;
  }, [doc]);

  /** What a set of sources is: their document types, or their file names when unclassified. */
  const sourceLabel = (ids?: string[]) => {
    const found = (ids ?? []).map((id) => sources.get(id)).filter(Boolean) as Array<{ category: DocumentCategory | null; name: string }>;
    const cats = Array.from(new Set(found.map((s) => s.category).filter(Boolean))) as DocumentCategory[];
    if (cats.length) return cats.map((c) => CATEGORY_LABELS[c] ?? c).join(', ');
    return Array.from(new Set(found.map((s) => s.name))).join(', ');
  };

  // Files grouped by what their parts are; a ZIP's members can fall in different groups.
  const groups = useMemo(() => {
    const byCat = new Map<string, Array<{ fileName: string; state: string; error?: string | null; part?: InboundDocumentPart }>>();
    for (const f of doc?.files ?? []) {
      if (f.state !== 'DONE' || !f.parts?.length) {
        const key = '__state';
        byCat.set(key, [...(byCat.get(key) ?? []), { fileName: f.name, state: f.state, error: f.error }]);
        continue;
      }
      for (const p of f.parts) {
        const key = p.category ?? (!p.path ? '__unsupported' : p.readError ? '__unread' : 'OTHER');
        byCat.set(key, [...(byCat.get(key) ?? []), { fileName: f.parts.length > 1 ? p.name : f.name, state: f.state, part: p }]);
      }
    }
    const ordered: Array<[string, typeof byCat extends Map<string, infer V> ? V : never]> = [];
    for (const c of CATEGORY_ORDER) if (byCat.has(c)) ordered.push([c, byCat.get(c)!]);
    for (const k of ['__unread', '__unsupported', '__state']) if (byCat.has(k)) ordered.push([k, byCat.get(k)!]);
    return ordered;
  }, [doc]);

  const reread = async () => {
    if (!doc) return;
    setRereading(true);
    try {
      const res = await diApi.requestDocumentReread(clientUid, doc.pro);
      if (res.success === false) {
        toastRef.current(res.message || 'Could not start the re-read', 'error');
        return;
      }
      toastRef.current(`Re-reading load ${doc.pro}. This takes a minute or two.`, 'success');
      setRereadPending(doc.pro);
      if (rereadTimer.current) clearTimeout(rereadTimer.current);
      rereadTimer.current = setTimeout(() => {
        rereadTimer.current = null;
        setRereadPending(null);
        setTick((t) => t + 1);
      }, 60_000);
    } catch (err) {
      toastRef.current(errorText(err, 'Could not start the re-read'), 'error');
    } finally {
      setRereading(false);
    }
  };

  const flags = doc?.flags ?? [];
  const serious = flags.filter((f) => f.level !== 'info');
  const info = flags.filter((f) => f.level === 'info');
  const missing = new Set(doc?.missingFields ?? []);
  const bol = doc?.summary?.bol ?? {};
  const signatures = (['shipperSigned', 'carrierSigned', 'consigneeSigned'] as const).filter((k) => bol[k] === true);

  return (
    <Drawer anchor="right" open={Boolean(pro)} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', md: 640 } } }}>
      <Stack sx={{ height: '100%' }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 3, py: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h5">Load {doc?.pro ?? pro}</Typography>
              {doc && <DocumentStatusChip status={doc.documentStatus} />}
            </Stack>
            {doc && (
              <Typography variant="caption" color="text.secondary" sx={mono}>
                {doc.ref}
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            {canRequest && doc && (
              <Tooltip
                title={
                  doc.documentStatus === 'PROCESSING' || rereadPending === doc.pro
                    ? 'This load is being read — check back in a minute'
                    : 'Read every file of this load again'
                }
              >
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    onClick={reread}
                    disabled={rereading || doc.documentStatus === 'PROCESSING' || rereadPending === doc.pro}
                  >
                    Re-read
                  </Button>
                </span>
              </Tooltip>
            )}
            <IconButton onClick={onClose} aria-label="Close load documents">
              <CloseCircle size={22} />
            </IconButton>
          </Stack>
        </Stack>

        <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2.5 }}>
          {loading && !doc && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} aria-label="Loading load documents" />
            </Box>
          )}
          {error && <Alert severity="error">{error}</Alert>}

          {doc && (
            <Stack spacing={3}>
              {/* Aljex + transaction */}
              <Stack spacing={0.5}>
                {!doc.aljex ? (
                  <Typography variant="body2" color="text.secondary">
                    Not checked against Aljex yet.
                  </Typography>
                ) : doc.aljex.found ? (
                  <Typography variant="body2">
                    In Aljex{doc.aljex.status ? ` · ${doc.aljex.status}` : ''}
                    {doc.aljex.deliveryDate ? ` · delivery ${doc.aljex.deliveryDate}` : ''}
                    {doc.aljex.consignee ? ` · ${doc.aljex.consignee}` : ''}
                    {doc.aljex.refMatches === false && (
                      <Typography component="span" variant="body2" color="error.main">
                        {' '}
                        · reference differs from Aljex ({doc.aljex.refNum})
                      </Typography>
                    )}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="warning.dark">
                    This load is not in Aljex.
                  </Typography>
                )}
                {doc.transaction?.authkeyUid ? (
                  <Link
                    component="button"
                    variant="body2"
                    sx={{ alignSelf: 'flex-start' }}
                    onClick={() =>
                      navigate(
                        `/transactions/details/${doc.transaction!.teamUid || '_'}/${doc.transaction!.authkeyUid}/${
                          doc.transaction!.key || ''
                        }`
                      )
                    }
                  >
                    Open transaction {doc.transaction.key || ''}
                  </Link>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    No transaction is linked to this load yet.
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  Last file {relativeTime(doc.lastFileAt)} · summary updated {formatDateTime(doc.summarisedAt)}
                </Typography>
              </Stack>

              {/* Flags */}
              {flags.length > 0 && (
                <Stack spacing={1}>
                  {serious.map((f, i) => (
                    <Alert key={`${f.code}-${i}`} severity={f.level === 'error' ? 'error' : 'warning'} sx={{ py: 0.25 }}>
                      {f.message}
                      {sourceLabel(f.sources) ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          From: {sourceLabel(f.sources)}
                        </Typography>
                      ) : null}
                    </Alert>
                  ))}
                  {info.length > 0 && (
                    <Box>
                      <Button size="small" onClick={() => setShowInfo((v) => !v)} sx={{ textTransform: 'none', px: 0.5 }}>
                        {showInfo ? 'Hide' : 'Show'} {info.length} note{info.length === 1 ? '' : 's'}
                      </Button>
                      {showInfo &&
                        info.map((f, i) => (
                          <Typography key={`${f.code}-${i}`} variant="body2" color="text.secondary" sx={{ pl: 1, py: 0.25 }}>
                            · {f.message}
                          </Typography>
                        ))}
                    </Box>
                  )}
                </Stack>
              )}

              {/* Key fields */}
              <Section title="Key details">
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 3, rowGap: 2 }}>
                  {FIELD_GROUPS.map((g) => (
                    <Box key={g.title}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}
                      >
                        {g.title}
                      </Typography>
                      {g.rows.map((r) => {
                        const value = get(doc.summary, r.path);
                        const from = sourceLabel(doc.fieldSources?.[r.path]);
                        const isKey = r.path in KEY_FIELD_LABELS;
                        return (
                          <Stack key={r.path} direction="row" spacing={1} alignItems="baseline" sx={{ py: 0.25 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ width: 96, flexShrink: 0 }}>
                              {r.label}
                            </Typography>
                            {value ? (
                              <Tooltip title={from ? `From: ${from}` : ''} placement="top-start">
                                <Typography variant="body2" sx={{ ...mono, fontSize: 13, wordBreak: 'break-word' }}>
                                  {value}
                                </Typography>
                              </Tooltip>
                            ) : (
                              <Typography variant="body2" color={isKey && missing.has(r.path) ? 'warning.dark' : 'text.disabled'}>
                                {isKey && missing.has(r.path) ? 'Missing' : '—'}
                              </Typography>
                            )}
                          </Stack>
                        );
                      })}
                      {g.title === 'Seal' && (doc.summary.sealNumbers?.length ?? 0) > 1 && (
                        <Typography variant="caption" color="text.secondary">
                          Also read: {doc.summary.sealNumbers!.filter((s) => s !== doc.summary.sealNumber).join(', ')}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </Section>

              {/* BOL */}
              {BOL_ROWS.some((r) => bol[r.key]) && (
                <Section title="Bill of lading">
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 3 }}>
                    {BOL_ROWS.filter((r) => bol[r.key]).map((r) => (
                      <Stack key={r.key} direction="row" spacing={1} alignItems="baseline" sx={{ py: 0.25 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ width: 96, flexShrink: 0 }}>
                          {r.label}
                        </Typography>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                          {String(bol[r.key])}
                        </Typography>
                      </Stack>
                    ))}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Signed by: {signatures.length ? signatures.map((k) => k.replace('Signed', '')).join(', ') : 'no signatures read'}
                  </Typography>
                </Section>
              )}

              {/* Licences */}
              {(doc.summary.driverLicensesOnFile?.length ?? 0) > 0 && (
                <Section title="Driver licences on file">
                  {doc.summary.driverLicensesOnFile!.map((l, i) => (
                    <Typography key={`${l.from}-${i}`} variant="body2">
                      {l.name || 'Name not read'}
                      <Box component="span" sx={{ ...mono, ml: 1 }}>
                        {l.licenseNumber || '—'}
                      </Box>
                      {l.licenseState ? ` · ${l.licenseState}` : ''}
                    </Typography>
                  ))}
                </Section>
              )}

              {/* Documents */}
              <Section title="Documents">
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                  {doc.documentsPresent.map((c) => (
                    <Chip key={c} size="small" color="success" variant="light" label={CATEGORY_LABELS[c] ?? c} />
                  ))}
                  {doc.documentsMissing.map((c) => (
                    <Chip key={c} size="small" color="warning" variant="outlined" label={`No ${(CATEGORY_LABELS[c] ?? c).toLowerCase()}`} />
                  ))}
                </Stack>
                <Stack spacing={2} divider={<Divider flexItem />}>
                  {groups.map(([cat, items]) => (
                    <Box key={cat}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}
                      >
                        {cat === '__state'
                          ? 'Not read'
                          : cat === '__unread'
                          ? 'Stored, could not be read'
                          : cat === '__unsupported'
                          ? 'Not stored (unsupported file type)'
                          : CATEGORY_LABELS[cat as DocumentCategory] ?? cat}{' '}
                        · {items.length}
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(132px, 1fr))', gap: 1.25, mt: 0.75 }}>
                        {items.map((it, i) => (
                          <Box
                            key={`${it.fileName}-${i}`}
                            sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden', bgcolor: 'background.paper' }}
                          >
                            {it.part?.url && isImage(it.part) ? (
                              <Link href={it.part.url} target="_blank" rel="noopener noreferrer" sx={{ display: 'block' }}>
                                <Box
                                  component="img"
                                  src={it.part.url}
                                  alt={it.fileName}
                                  loading="lazy"
                                  sx={{ display: 'block', width: '100%', height: 96, objectFit: 'cover', bgcolor: 'grey.100' }}
                                />
                              </Link>
                            ) : it.part?.url ? (
                              <Link
                                href={it.part.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                underline="none"
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  height: 96,
                                  bgcolor: 'grey.50',
                                  color: 'text.secondary'
                                }}
                              >
                                <Stack alignItems="center" spacing={0.5}>
                                  <DocumentText size={28} />
                                  <Typography variant="caption">Open PDF</Typography>
                                </Stack>
                              </Link>
                            ) : (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  height: 96,
                                  bgcolor: 'grey.50',
                                  px: 1
                                }}
                              >
                                <Typography variant="caption" color="text.secondary" textAlign="center">
                                  {it.part
                                    ? it.part.readError || 'Not stored'
                                    : FILE_STATE_LABELS[it.state as keyof typeof FILE_STATE_LABELS] ?? it.state}
                                </Typography>
                              </Box>
                            )}
                            <Tooltip title={it.error || it.part?.readError || it.fileName}>
                              <Typography variant="caption" sx={{ display: 'block', px: 0.75, py: 0.5, ...mono, fontSize: 10.5 }} noWrap>
                                {it.fileName}
                              </Typography>
                            </Tooltip>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  File links work for 10 minutes; reopen the load for fresh ones.
                </Typography>
              </Section>

              {/* Everything else that was read */}
              {(doc.summary.otherFields?.length ?? 0) > 0 && (
                <Section
                  title="Other details read"
                  action={
                    <Button size="small" onClick={() => setShowOther((v) => !v)} sx={{ textTransform: 'none' }}>
                      {showOther ? 'Hide' : `Show ${doc.summary.otherFields!.length}`}
                    </Button>
                  }
                >
                  {showOther &&
                    doc.summary.otherFields!.map((o, i) => (
                      <Stack key={`${o.from}-${i}`} direction="row" spacing={1} alignItems="baseline" sx={{ py: 0.25 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ width: 160, flexShrink: 0 }}>
                          {o.label}
                        </Typography>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word', flex: 1 }}>
                          {o.value}
                        </Typography>
                        <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0 }}>
                          {sourceLabel([o.from])}
                        </Typography>
                      </Stack>
                    ))}
                </Section>
              )}
            </Stack>
          )}
        </Box>
      </Stack>
    </Drawer>
  );
}
